import { Col, Form, Input, InputNumber, Modal, Row, Select, Upload, } from "antd";
import { useForm } from "antd/lib/form/Form";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EntOrganicproducers, EntLivestockProduction } from "../../../../models/organic/organicProducers";
import { DraggableModal } from "../../../../components/DraggableModal";
import SelectTerritorialUnitsCode from "../../../../components/selects/SelectTerritorialUnitsCode";
import { MapAddSingleMarker } from "../../../../components/maps/MapAddPoint";
import { GeoJsonPoint } from "../../../../models/geoJsonPoint";
import { Geometry } from "../../../../models/geoJson";
import SelectProductType from "../../../../components/selects/SelectProductType";
import { UploadOutlined } from "@ant-design/icons";
import GeneralSelect from "../../../../components/selects/general/GeneralSelect";
import { EntBasicHB } from "../../../../models/handBooks/basicHB";
import { EntDirectoryInfoName } from "../../../../models/directoryInfoName";
import { EntInitialFilter } from "../../../../models/filters/initialFilter";
import { EntUnitMeasurementFilter } from "../../../../models/filters/unitMeasurementFilter";
import SelectProductionMethods from "../../../../components/selects/SelectProductionMethods";
import { EntUnitMeasurement } from "../../../../models/handBooks/unitMeasurement";
import { useLazyGetRegisterStatusListQuery } from "../../../../redux/api/handBooksApi/organic/registerStatusApi";
import { useLazyGetLegalFormListQuery } from "../../../../redux/api/handBooksApi/organic/legalFormApi";
import { useLazyGetUnitMeasurementListQuery } from "../../../../redux/api/handBooksApi/unitMeasurementApi";
import { useLazyGetAnimalTypeListQuery } from "../../../../redux/api/handBooksApi/animalTypesApi";

enum FormField {
  regionId,
  areaId,
  territorialUnitId,
  name,
  legalFormId,
  registrationDate,
  certificationBody,
  certificationNumber,
  validUntil,
  statusId,
  latitude,   
  longitude,
  // Livestock Production fields
  year,
  animalTypeId,
  productTypeId,
  quantity,
  unitMeasurementId,
  productionMethodId,
  files,
}

interface IProps {
  show: boolean;
  item: EntOrganicproducers;
  onHide: () => void;
  onSubmit: (pos: EntOrganicproducers) => Promise<boolean>;
}

export const AddOrganicproducers = (props: IProps) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [deleteFileIds, setDeleteFileIds] = useState<number[]>([]);

  const [geoJsonData, setGeoJsonData] = useState<any>(null);
  const [selectedRegionPolygonJson, setSelectedRegionPolygonJson] = useState<string|null>(null);

  const [selectedRegionId, setSelectedRegionId] = useState<number|null>(null);
  const [selectedRegionCode, setSelectedRegionCode] = useState<string|null>(null);
  const [selectedAreaCode, setSelectedAreaCode] = useState<string|null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<number|null>(null);

  useEffect(() => {
	if (props.show) {
	  setFieldsFromEntity();
	  // 🔥 Подставляем файлы в Upload
	  if (props.item.files) {
		const filesForUpload = props.item.files.map((f) => ({
		  uid: f.id,
		  name: f.name,
		  status: "done",
		  url: f.path,
		}));
		setFileList(filesForUpload);
	  } else {
		setFileList([]);
	  }
	}
	setSelectedRegionId(props.item.region?.id || null);
	setSelectedAreaId(props.item.area?.id || null);
	setSelectedAreaCode(props.item.territorialUnit?.code || null);
  }, [props.show]);

  const setFieldsFromEntity = (): void => {
	form.setFields([
	  { name: FormField.regionId, value: props.item.region?.id },
	  { name: FormField.areaId, value: props.item.area?.id },
	  { name: FormField.territorialUnitId, value: props.item.territorialUnit?.id },
	  { name: FormField.name, value: props.item.name },
	  { name: FormField.legalFormId, value: props.item.legalForm?.id },
	  { name: FormField.registrationDate, value: props.item.registrationDate },
	  { name: FormField.certificationBody, value: props.item.certificationBody },
	  { name: FormField.certificationNumber, value: props.item.certificationNumber },
	  { name: FormField.validUntil, value: props.item.validUntil },
	  { name: FormField.statusId, value: props.item.status?.id },
	  // Livestock Production fields (берем первый элемент массива)
	  { name: FormField.year, value: props.item.livestockProductions?.[0]?.year },
	  { name: FormField.animalTypeId, value: props.item.livestockProductions?.[0]?.animalType?.id },
	  { name: FormField.productTypeId, value: props.item.livestockProductions?.[0]?.productType?.id },
	  { name: FormField.quantity, value: props.item.livestockProductions?.[0]?.quantity },
	  { name: FormField.unitMeasurementId, value: props.item.livestockProductions?.[0]?.unitMeasurement?.id },
	  { name: FormField.productionMethodId, value: props.item.livestockProductions?.[0]?.productionMethod?.id },
	]);
  };

  const updateGeoJsonPoint = () => {
	const latitude = props.item.latitude;
	const longitude = props.item.longitude;

	if (latitude !== undefined && longitude !== undefined) {
	  const geoJsonPoint = new GeoJsonPoint();
	  geoJsonPoint.geometry = new Geometry();
	  geoJsonPoint.geometry.coordinates = [longitude, latitude];
	  setGeoJsonData(geoJsonPoint);
	} else {
	  console.error(t('breeding_farms.error_geometry'));
	}
  };

  useEffect(() => {
	if (props.item.latitude && props.item.longitude) {
	  updateGeoJsonPoint();
	} else {
	  setGeoJsonData(null);
	}
  }, [props.item.latitude, props.item.longitude]);

  const setEntityFromFields = (): EntOrganicproducers => {
	let res = _.cloneDeep(props.item);
	res.regionId = form.getFieldValue(FormField.regionId);
	res.areaId = form.getFieldValue(FormField.areaId);
	res.territorialUnitId = form.getFieldValue(FormField.territorialUnitId);
	res.name = form.getFieldValue(FormField.name);
	res.legalFormId = form.getFieldValue(FormField.legalFormId);
	res.registrationDate = form.getFieldValue(FormField.registrationDate);
	res.certificationBody = form.getFieldValue(FormField.certificationBody);
	res.certificationNumber = form.getFieldValue(FormField.certificationNumber);
	res.validUntil = form.getFieldValue(FormField.validUntil);
	res.statusId = form.getFieldValue(FormField.statusId);

	// Создаем или обновляем livestock production
	if (!res.livestockProductions || res.livestockProductions.length === 0) {
	  res.livestockProductions = [new EntLivestockProduction()];
	}
	
	const production = res.livestockProductions[0];
	production.year = form.getFieldValue(FormField.year);
	production.animalTypeId = form.getFieldValue(FormField.animalTypeId);
	production.productTypeId = form.getFieldValue(FormField.productTypeId);
	production.quantity = form.getFieldValue(FormField.quantity);
	production.unitMeasurementId = form.getFieldValue(FormField.unitMeasurementId);
	production.productionMethodId = form.getFieldValue(FormField.productionMethodId);
	production.producerId = res.id;

	if (geoJsonData !== null) {
	  res.latitude = geoJsonData.geometry?.coordinates[1] as number;
	  res.longitude = geoJsonData.geometry?.coordinates[0] as number;
	}
	res.files = fileList.map((file) => file.originFileObj || file);
	res.deleteFileIds = deleteFileIds;

	return res;
  };

  const hideModal = () => {
	props.onHide();
	setSelectedRegionId(null);
	setSelectedRegionPolygonJson(null);
	setSelectedAreaCode(null);
	setGeoJsonData(null);
	setFileList([]);
	setDeleteFileIds([]);
  };

  const handleSubmit = async () => {
	setLoading(true);
	try {
	  await form.validateFields();
	  if (geoJsonData !== null) {
		const data = setEntityFromFields();
		const result = await props.onSubmit(data);
		if (result) hideModal();
	  } else {
		Modal.error({
		  title: t("msg.error"),
		  content: t("breeding_farms.choose_location"),
		});
	  }
	} finally {
	  setLoading(false);
	}
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, index) => currentYear - index);

  return (
	<DraggableModal
	  title={ props.item.id === null ? t("general.add") : t("general.edit") }
	  visible={ props.show }
	  onCancel={ hideModal }
	  onSubmit={ handleSubmit }
	  submitLoading={ loading }
	  width={ 800 }
	>
	  <Form
		initialValues={ {
		  remember: true,
		} }
		autoComplete="off"
		layout="vertical"
		form={ form }
	  >
		<Row style={ { margin: 0 } } gutter={ 16 }>
		  <Col className="gutter-row" span={ 12 }>
			<Form.Item
			  label="Название производителя"
			  name={ FormField.name }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Input placeholder="Введите название производителя"/>
			</Form.Item>
			<Form.Item
			  label="Юридическая форма"
			  name={ FormField.legalFormId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <GeneralSelect<EntDirectoryInfoName, EntInitialFilter>
				queryHook={ useLazyGetLegalFormListQuery }
				initialFilter={ new EntInitialFilter() }
			  />
			</Form.Item>
			<Form.Item
			  label="Дата регистрации"
			  name={ FormField.registrationDate }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Input type="date" placeholder="Выберите дату"/>
			</Form.Item>
			<Form.Item
			  label="Орган сертификации"
			  name={ FormField.certificationBody }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Input placeholder="Введите орган сертификации"/>
			</Form.Item>
			<Form.Item
			  label="Номер сертификата"
			  name={ FormField.certificationNumber }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Input placeholder="Введите номер сертификата"/>
			</Form.Item>
			<Form.Item
			  label="Действителен до"
			  name={ FormField.validUntil }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Input type="date" placeholder="Выберите дату"/>
			</Form.Item>
			<Form.Item
			  label="Статус"
			  name={ FormField.statusId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <GeneralSelect<EntBasicHB, EntInitialFilter>
				queryHook={ useLazyGetRegisterStatusListQuery }
				initialFilter={ new EntInitialFilter() }
			  />
			</Form.Item>
			<Form.Item
			  label={ t("titles.territorial_unit_area") }
			  name={ FormField.regionId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <SelectTerritorialUnitsCode
				parentId={ 1 }
				onChange={ (value, option: any) => {
				  setSelectedRegionId(Number(value));
				  setSelectedRegionPolygonJson(option?.data?.polygonJson);
				  setSelectedRegionCode(option?.data?.code);
				  setSelectedAreaCode(null);
				  setSelectedAreaId(null);
				  form.setFieldsValue({
					[FormField.regionId]: value,
					[FormField.areaId]: null,
					[FormField.territorialUnitId]: null,
				  });
				} }
			  />
			</Form.Item>
			{ selectedRegionId || props.item.regionId ? (
			  <Form.Item
				label={ t("titles.territorialUnitDistricts") }
				name={ FormField.areaId }
				rules={ [
				  { required: true, message: t("msg.fill_in_the_field") },
				] }
			  >
				<SelectTerritorialUnitsCode
				  type={ ["AREA", "CITY_OF_REPUBLICAN_SIGNIFICANCE"] }
				  parentId={ selectedRegionId }
				  onChange={ (value, option: any) => {
					setSelectedRegionPolygonJson(option?.data?.polygonJson);
					setSelectedAreaCode(option?.data?.code || null);
					setSelectedAreaId(value as number);
					form.setFields([
					  { name: FormField.territorialUnitId, value: null },
					]);
				  } }
				/>
			  </Form.Item>
			) : null }
			{ selectedAreaCode || props.item.territorialUnitId ? (
			  <Form.Item
				label={ t("titles.territorialUnitVillage") }
				name={ FormField.territorialUnitId }
				rules={ [
				  { required: true, message: t("msg.fill_in_the_field") },
				] }
			  >
				<SelectTerritorialUnitsCode areaCode={ selectedAreaCode }/>
			  </Form.Item>
			) : null }
		  </Col>
		  <Col className="gutter-row" span={ 12 }>
			<Form.Item
			  label="Год производства"
			  name={ FormField.year }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Select placeholder="Выберите год" allowClear>
				{ years.map((year) => (
				  <Select.Option key={ year } value={ year }>
					{ year }
				  </Select.Option>
				)) }
			  </Select>
			</Form.Item>
			<Form.Item
			  label="Тип животного"
			  name={ FormField.animalTypeId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <GeneralSelect<EntBasicHB, EntInitialFilter>
				queryHook={ useLazyGetAnimalTypeListQuery }
				initialFilter={ new EntInitialFilter() }
			  />
			</Form.Item>
			<Form.Item 
			  label="Тип продукции" 
			  name={ FormField.productTypeId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <SelectProductType/>
			</Form.Item>
			<Form.Item 
			  label="Количество" 
			  name={ FormField.quantity }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <InputNumber
				style={ { width: "100%" } }
				min={ 0 }
				step={ 0.1 }
				placeholder="Введите количество"
			  />
			</Form.Item>
			<Form.Item
			  label="Единица измерения"
			  name={ FormField.unitMeasurementId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <GeneralSelect<EntUnitMeasurement, EntUnitMeasurementFilter>
				queryHook={ useLazyGetUnitMeasurementListQuery }
				initialFilter={ new EntUnitMeasurementFilter() }
			  />
			</Form.Item>
			<Form.Item
			  label="Метод производства"
			  name={ FormField.productionMethodId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <SelectProductionMethods/>
			</Form.Item>
		  </Col>
		</Row>
		<Row style={ { marginTop: 16 } }>
		  <Col span={ 24 }>
			<Form.Item label="Файлы" name={FormField.files}>
			  <Upload
				fileList={fileList}
				onChange={({ fileList }) => setFileList(fileList)}
				beforeUpload={() => false}
				multiple
				accept="image/*,.pdf,.doc,.docx"
				listType="picture-card"
				onRemove={(file) => {
				  if (file.uid && !file.originFileObj) {
					setDeleteFileIds((prev) => [...prev, Number(file.uid)]);
				  }
				}}
			  >
				{fileList.length >= 8 ? null : (
				  <div>
					<UploadOutlined />
					<div style={{ marginTop: 8 }}>Загрузить</div>
				  </div>
				)}
			  </Upload>
			</Form.Item>
		  </Col>
		</Row>
		<MapAddSingleMarker
		  polygonJson={ selectedRegionPolygonJson }
		  setGeoJsonData={ setGeoJsonData }
		  geoJsonData={ geoJsonData }
		/>
	  </Form>
	</DraggableModal>
  );
};
